import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useChatAutoScroll } from '../hooks/useChatAutoScroll';
import {
  useAdminMessagingConversations,
  useAdminMessagingSellers,
  useAdminConversationMessages,
  useAdminEmailBroadcasts,
  useAdminEmailBroadcastDetail,
  useAdminMessagingInvalidation,
} from '../hooks/queries/adminMessagingQueries';
import {
  MessageSquare,
  Mail,
  Send,
  Loader2,
  Store,
  Users,
  User,
  Search,
  ArrowLeft,
  Plus,
  CheckCircle2,
  AlertCircle,
  Inbox,
} from 'lucide-react';
import { Button } from '../components/ui';

type Tab = 'sellers' | 'email';
type MobilePanel = 'list' | 'detail';

interface SellerRow {
  id: string;
  name: string;
  logo?: string;
}

interface ConversationRow {
  id: string;
  seller?: { id?: string; name?: string; logo?: string };
  lastMessage?: string;
  lastMessageAt?: string;
  unreadByAdmin?: number;
}

interface ChatMessage {
  id?: string;
  text?: string;
  message?: string;
  type: string;
  createdAt: string;
  senderId?: string | null;
  sender?: { firstName?: string; lastName?: string };
}

interface EmailBroadcastRow {
  id: string;
  subject: string;
  audience: 'buyers' | 'sellers' | 'everyone';
  sent: number;
  total: number;
  failed: number;
  bodyPreview?: string;
  createdAt: string;
}

interface EmailBroadcastDetail extends EmailBroadcastRow {
  text?: string;
  html?: string;
  failures?: string[];
}

const cx = (...classes: (string | false | null | undefined)[]) =>
  classes.filter(Boolean).join(' ');

const formatWhen = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
};

const initials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

function EmptyState({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-custom-accent-light text-custom-primary">
        <Icon size={28} strokeWidth={1.5} />
      </div>
      <p className="text-base font-semibold text-gray-900">{title}</p>
      <p className="mt-1 max-w-sm text-sm text-gray-500">{description}</p>
    </div>
  );
}

function SidebarSkeleton() {
  return (
    <div className="space-y-2 p-3">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="animate-pulse rounded-xl bg-gray-100 h-16" />
      ))}
    </div>
  );
}

const AdminMessaging: React.FC = () => {
  const { t } = useTranslation('messaging');
  const [tab, setTab] = useState<Tab>('sellers');
  const [chatMobilePanel, setChatMobilePanel] = useState<MobilePanel>('list');
  const [emailMobilePanel, setEmailMobilePanel] = useState<MobilePanel>('list');

  const { invalidateConversations, invalidateMessages, invalidateEmails } =
    useAdminMessagingInvalidation();

  const [conversationSearch, setConversationSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeSellerId, setActiveSellerId] = useState('');
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const [emailAudience, setEmailAudience] = useState<'buyers' | 'sellers' | 'everyone'>('buyers');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState<{ type: 'success' | 'warning' | 'error'; text: string } | null>(null);
  const [emailSearch, setEmailSearch] = useState('');
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [emailComposeMode, setEmailComposeMode] = useState(false);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const emailSubjectRef = useRef<HTMLInputElement>(null);
  const emailBodyRef = useRef<HTMLTextAreaElement>(null);

  const sellersTabActive = tab === 'sellers';
  const emailTabActive = tab === 'email';

  const {
    data: conversations = [],
    isLoading: loadingList,
    isFetching: fetchingConversations,
  } = useAdminMessagingConversations(sellersTabActive);

  const { data: sellers = [], isLoading: loadingSellers } =
    useAdminMessagingSellers(sellersTabActive);

  const {
    data: messagePayload,
    isLoading: loadingMessages,
    isFetching: fetchingMessages,
  } = useAdminConversationMessages(selectedId, sellersTabActive && !!selectedId);

  const messages = (messagePayload?.messages || []) as ChatMessage[];
  const sellerUserId = messagePayload?.sellerUserId ?? null;

  const { data: emailBroadcasts = [], isLoading: loadingEmailList, isFetching: fetchingEmails } =
    useAdminEmailBroadcasts(emailTabActive);

  const { data: selectedEmailData = null, isLoading: loadingEmailDetail } =
    useAdminEmailBroadcastDetail(
      selectedEmailId,
      emailTabActive && !!selectedEmailId && !emailComposeMode
    );
  const selectedEmail = selectedEmailData as EmailBroadcastDetail | null;
  const typedConversations = conversations as ConversationRow[];
  const typedSellers = sellers as SellerRow[];
  const typedEmailBroadcasts = emailBroadcasts as EmailBroadcastRow[];

  const { scrollToBottom } = useChatAutoScroll({
    containerRef: messagesContainerRef,
    conversationKey: selectedId,
    messageCount: messages.length,
    enabled: !!selectedId && !loadingMessages,
  });

  const selectedConv = typedConversations.find((c) => c.id === selectedId);

  useEffect(() => {
    if (selectedConv?.seller?.id) setActiveSellerId(selectedConv.seller.id);
  }, [selectedId, selectedConv?.seller?.id]);

  const filteredConversations = useMemo(() => {
    const q = conversationSearch.trim().toLowerCase();
    if (!q) return typedConversations;
    return typedConversations.filter((c) => {
      const name = (c.seller?.name || '').toLowerCase();
      const preview = (c.lastMessage || '').toLowerCase();
      return name.includes(q) || preview.includes(q);
    });
  }, [typedConversations, conversationSearch]);

  const filteredEmails = useMemo(() => {
    const q = emailSearch.trim().toLowerCase();
    if (!q) return typedEmailBroadcasts;
    return typedEmailBroadcasts.filter((e) => {
      const subject = e.subject.toLowerCase();
      const preview = (e.bodyPreview || '').toLowerCase();
      return subject.includes(q) || preview.includes(q);
    });
  }, [typedEmailBroadcasts, emailSearch]);

  const audienceLabel = (audience: EmailBroadcastRow['audience']) => {
    if (audience === 'buyers') return t('buyers');
    if (audience === 'sellers') return t('sellersAudience');
    return t('everyone');
  };

  const openSellerConversation = async (
    sellerId: string,
    options?: { text?: string; selectOnly?: boolean }
  ): Promise<string | null> => {
    if (!sellerId) return null;

    const existing = typedConversations.find((c) => c.seller?.id === sellerId);
    if (existing && !options?.text) {
      setSelectedId(existing.id);
      setActiveSellerId(sellerId);
      setChatMobilePanel('detail');
      await invalidateMessages();
      return existing.id;
    }

    try {
      const res = await api.post(`/admin/messaging/conversations/seller/${sellerId}`, {
        ...(options?.text ? { text: options.text } : {}),
      });
      const conv = res.data.data;
      const id = conv?.id || conv?._id;
      if (id) {
        setSelectedId(id);
        setActiveSellerId(sellerId);
        setChatMobilePanel('detail');
        await invalidateMessages();
        if (!options?.selectOnly) scrollToBottom('smooth');
        return id;
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      alert(err.response?.data?.message || t('openFailed'));
    }
    return null;
  };

  const handleSellerSelect = async (sellerId: string) => {
    setActiveSellerId(sellerId);
    if (!sellerId) {
      setSelectedId(null);
      setChatMobilePanel('list');
      return;
    }
    await openSellerConversation(sellerId, { selectOnly: true });
  };

  const handleConversationClick = (conv: ConversationRow) => {
    setSelectedId(conv.id);
    if (conv.seller?.id) setActiveSellerId(conv.seller.id);
    setChatMobilePanel('detail');
  };

  const handleSendChat = async () => {
    const text = replyText.trim();
    if (!text) return;
    if (!activeSellerId) {
      alert(t('selectSellerRequired'));
      return;
    }

    setSending(true);
    try {
      let conversationId = selectedId;

      if (!conversationId || selectedConv?.seller?.id !== activeSellerId) {
        const existing = typedConversations.find((c) => c.seller?.id === activeSellerId);
        if (existing) {
          conversationId = existing.id;
          setSelectedId(existing.id);
        } else {
          conversationId = await openSellerConversation(activeSellerId, { text });
          if (conversationId) setReplyText('');
          return;
        }
      }

      if (!conversationId) {
        conversationId = await openSellerConversation(activeSellerId, { text });
        if (conversationId) setReplyText('');
        return;
      }

      await api.post(`/admin/messaging/conversations/${conversationId}/messages`, { text });
      setReplyText('');
      await invalidateMessages();
      scrollToBottom('smooth');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      alert(err.response?.data?.message || t('sendFailed'));
    } finally {
      setSending(false);
    }
  };

  const handleBroadcastEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailResult(null);
    setEmailSending(true);
    try {
      const res = await api.post(
        '/admin/messaging/email/broadcast',
        {
          audience: emailAudience,
          subject: emailSubject.trim(),
          text: emailBody.trim(),
        },
        { timeout: 120000 }
      );
      const data = res.data?.data ?? res.data;
      const sent = Number(data?.sent) || 0;
      const total = Number(data?.total) || sent;
      const failed = Number(data?.failed) || 0;

      if (failed > 0) {
        setEmailResult({
          type: 'warning',
          text: t('emailSentPartial', { sent, total, failed }),
        });
      } else {
        setEmailResult({
          type: 'success',
          text: t('emailSent', { count: sent, total }),
        });
      }

      setEmailSubject('');
      setEmailBody('');
      setEmailComposeMode(false);
      setEmailMobilePanel('detail');
      if (data?.id) setSelectedEmailId(String(data.id));
      await invalidateEmails();
    } catch (err: unknown) {
      const e = err as {
        code?: string;
        response?: { data?: { message?: string; data?: { sent?: number; total?: number } } };
      };
      if (e.code === 'ECONNABORTED') {
        setEmailResult({ type: 'warning', text: t('emailTimeout') });
        return;
      }
      const msg = e.response?.data?.message || 'Failed to send email';
      const partial = e.response?.data?.data;
      if (partial && Number(partial.sent) > 0) {
        setEmailResult({
          type: 'warning',
          text: t('emailSentPartial', {
            sent: partial.sent,
            total: partial.total ?? partial.sent,
            failed: (partial.total ?? 0) - (partial.sent ?? 0),
          }),
        });
        return;
      }
      if (msg.includes('SMTP') || msg.includes('not configured')) {
        setEmailResult({ type: 'error', text: t('emailNotConfigured') });
        return;
      }
      setEmailResult({ type: 'error', text: msg });
    } finally {
      setEmailSending(false);
    }
  };

  const hasEmailDraft = Boolean(emailSubject.trim() || emailBody.trim());

  const resetComposeDraft = () => {
    setEmailSubject('');
    setEmailBody('');
    setEmailAudience('buyers');
    setEmailResult(null);
  };

  const focusCompose = () => {
    requestAnimationFrame(() => {
      if (!emailSubject.trim()) {
        emailSubjectRef.current?.focus();
      } else {
        emailBodyRef.current?.focus();
      }
    });
  };

  /** Gmail-like Compose: focus if empty draft; confirm discard if editing; else fresh compose */
  const startNewEmail = () => {
    if (emailComposeMode) {
      if (!hasEmailDraft) {
        focusCompose();
        setEmailMobilePanel('detail');
        return;
      }
      if (!window.confirm(t('discardDraft'))) {
        focusCompose();
        return;
      }
      resetComposeDraft();
      focusCompose();
      setEmailMobilePanel('detail');
      return;
    }

    resetComposeDraft();
    setEmailComposeMode(true);
    setSelectedEmailId(null);
    setEmailMobilePanel('detail');
    focusCompose();
  };

  const openSentEmail = (id: string) => {
    setEmailComposeMode(false);
    setSelectedEmailId(id);
    setEmailResult(null);
    setEmailMobilePanel('detail');
  };

  const switchTab = (next: Tab) => {
    setTab(next);
    setChatMobilePanel('list');
    setEmailMobilePanel('list');
  };

  const isAdminMessage = (msg: ChatMessage) =>
    Boolean(sellerUserId && msg.senderId && msg.senderId !== sellerUserId);

  const shellHeight = 'min-h-[calc(100dvh-11rem)] max-h-[calc(100dvh-11rem)]';

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-custom-primary to-custom-secondary text-white shadow-sm">
            <MessageSquare size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
              {t('title')}
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 sm:text-base">{t('subtitle')}</p>
            {(fetchingConversations || fetchingMessages || fetchingEmails) && (
              <p className="text-xs text-gray-400 mt-1">Updating…</p>
            )}
          </div>
        </div>

        {/* Segmented tabs */}
        <div className="inline-flex self-start rounded-2xl bg-gray-100 p-1 shadow-inner">
          {(
            [
              { id: 'sellers' as const, icon: MessageSquare, label: t('tabSellers') },
              { id: 'email' as const, icon: Mail, label: t('tabEmail') },
            ] as const
          ).map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => switchTab(id)}
              className={cx(
                'flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all',
                tab === id
                  ? 'bg-white text-custom-primary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              <Icon size={16} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Chat tab */}
      {tab === 'sellers' && (
        <div
          className={cx(
            'grid overflow-hidden rounded-2xl border border-gray-200 bg-gray-50/80 shadow-card',
            shellHeight,
            'grid-cols-1 lg:grid-cols-[minmax(280px,340px)_1fr]'
          )}
        >
          {/* Conversations sidebar */}
          <aside
            className={cx(
              'flex min-h-0 flex-col border-gray-200 bg-white lg:border-r',
              chatMobilePanel === 'detail' && 'hidden lg:flex'
            )}
          >
            <div className="border-b border-gray-100 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                {t('recentConversations')}
              </p>
              <div className="relative mt-3">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  type="search"
                  value={conversationSearch}
                  onChange={(e) => setConversationSearch(e.target.value)}
                  placeholder={t('searchConversations', { defaultValue: 'Search…' })}
                  className="w-full rounded-xl border-0 bg-gray-100 py-2.5 pl-9 pr-3 text-sm text-gray-800 placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-custom-primary/20"
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {loadingList && typedConversations.length === 0 ? (
                <SidebarSkeleton />
              ) : filteredConversations.length === 0 ? (
                <p className="p-4 text-center text-sm text-gray-500">
                  {conversationSearch ? t('noSearchResults', { defaultValue: 'No results' }) : t('noConversations')}
                </p>
              ) : (
                <ul className="p-2 space-y-1">
                  {filteredConversations.map((conv) => {
                    const name = conv.seller?.name || t('seller');
                    const active = selectedId === conv.id;
                    return (
                      <li key={conv.id}>
                        <button
                          type="button"
                          onClick={() => handleConversationClick(conv)}
                          className={cx(
                            'flex w-full gap-3 rounded-xl p-3 text-left transition-colors',
                            active
                              ? 'bg-custom-accent-light ring-1 ring-custom-secondary/30'
                              : 'hover:bg-gray-50'
                          )}
                        >
                          <div
                            className={cx(
                              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                              active
                                ? 'bg-custom-primary text-white'
                                : 'bg-gray-200 text-gray-700'
                            )}
                          >
                            {initials(name)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate font-medium text-gray-900">{name}</span>
                              <span className="shrink-0 text-[11px] text-gray-400">
                                {conv.lastMessageAt ? formatWhen(conv.lastMessageAt) : ''}
                              </span>
                            </div>
                            <p className="mt-0.5 truncate text-xs text-gray-500">
                              {conv.lastMessage || '—'}
                            </p>
                          </div>
                          {(conv.unreadByAdmin || 0) > 0 && (
                            <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-custom-secondary px-1.5 text-[10px] font-bold text-white">
                              {conv.unreadByAdmin}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>

          {/* Chat panel */}
          <section
            className={cx(
              'flex min-h-0 min-w-0 flex-col bg-white',
              chatMobilePanel === 'list' && 'hidden lg:flex'
            )}
          >
            <header className="flex shrink-0 items-center gap-3 border-b border-gray-100 px-4 py-3 sm:px-5">
              <button
                type="button"
                onClick={() => setChatMobilePanel('list')}
                className="lg:hidden flex h-9 w-9 items-center justify-center rounded-xl text-gray-600 hover:bg-gray-100"
                aria-label={t('backToList', { defaultValue: 'Back' })}
              >
                <ArrowLeft size={18} />
              </button>
              <div className="min-w-0 flex-1 space-y-2">
                <select
                  className="w-full max-w-md rounded-xl border-0 bg-gray-100 px-3 py-2.5 text-sm text-gray-800 focus:bg-white focus:ring-2 focus:ring-custom-primary/20"
                  value={activeSellerId}
                  onChange={(e) => handleSellerSelect(e.target.value)}
                  disabled={loadingSellers || sending}
                >
                  <option value="">{t('selectSeller')}</option>
                  {typedSellers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {activeSellerId && (
                  <p className="truncate text-xs text-gray-500">{t('supportThread')}</p>
                )}
              </div>
            </header>

            {!activeSellerId ? (
              <EmptyState
                icon={Store}
                title={t('selectConversation')}
                description={t('selectSellerToChat')}
              />
            ) : (
              <>
                <div
                  ref={messagesContainerRef}
                  className="min-h-0 flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/80 to-white px-4 py-4 sm:px-6"
                  style={{ overflowAnchor: 'none' }}
                >
                  {loadingMessages ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-custom-primary" />
                    </div>
                  ) : messages.length === 0 ? (
                    <p className="py-12 text-center text-sm text-gray-400">{t('noMessagesYet')}</p>
                  ) : (
                    <div className="space-y-3">
                      {messages.map((msg, i) => {
                        const fromAdmin = isAdminMessage(msg);
                        const body = msg.text || msg.message || '';
                        if (!body && msg.type !== 'TEXT') return null;
                        return (
                          <div
                            key={msg.id || i}
                            className={cx('flex', fromAdmin ? 'justify-end' : 'justify-start')}
                          >
                            <div
                              className={cx(
                                'max-w-[85%] sm:max-w-[70%] rounded-2xl px-4 py-2.5 shadow-sm',
                                fromAdmin
                                  ? 'rounded-br-md bg-custom-primary text-white'
                                  : 'rounded-bl-md border border-gray-100 bg-white text-gray-900'
                              )}
                            >
                              <p className="whitespace-pre-wrap text-sm leading-relaxed">{body}</p>
                              <p
                                className={cx(
                                  'mt-1 text-[10px]',
                                  fromAdmin ? 'text-white/70' : 'text-gray-400'
                                )}
                              >
                                {msg.createdAt
                                  ? new Date(msg.createdAt).toLocaleString()
                                  : ''}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <footer className="shrink-0 border-t border-gray-100 bg-white p-3 sm:p-4">
                  <div className="flex gap-2 rounded-2xl bg-gray-100 p-1.5 focus-within:bg-white focus-within:ring-2 focus-within:ring-custom-primary/20">
                    <input
                      type="text"
                      className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                      placeholder={t('replyPlaceholder')}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendChat()}
                      disabled={sending}
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="shrink-0 !rounded-xl !px-4"
                      onClick={handleSendChat}
                      disabled={sending || !replyText.trim()}
                      loading={sending}
                      icon={Send}
                      iconPosition="right"
                    >
                      <span className="sr-only sm:not-sr-only">{t('send', { defaultValue: 'Send' })}</span>
                    </Button>
                  </div>
                </footer>
              </>
            )}
          </section>
        </div>
      )}

      {/* Email tab — Gmail-style: list + read / compose pane */}
      {tab === 'email' && (
        <div
          className={cx(
            'grid overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-card',
            shellHeight,
            'grid-cols-1 lg:grid-cols-[minmax(300px,380px)_1fr]'
          )}
        >
          {/* Left: Compose + inbox list */}
          <aside
            className={cx(
              'flex min-h-0 flex-col bg-gray-50/50 lg:border-r lg:border-gray-200',
              emailMobilePanel === 'detail' && 'hidden lg:flex'
            )}
          >
            <div className="p-3 sm:p-4">
              <button
                type="button"
                onClick={startNewEmail}
                className={cx(
                  'flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg',
                  emailComposeMode
                    ? 'bg-custom-secondary hover:bg-custom-secondary-hover ring-2 ring-custom-secondary/40 ring-offset-2'
                    : 'bg-custom-primary hover:bg-custom-primary-hover'
                )}
              >
                <Plus size={20} strokeWidth={2.5} />
                {t('compose', { defaultValue: 'Compose' })}
              </button>
            </div>

            <div className="px-3 pb-3 sm:px-4">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  type="search"
                  value={emailSearch}
                  onChange={(e) => setEmailSearch(e.target.value)}
                  placeholder={t('searchEmails')}
                  className="w-full rounded-full border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm shadow-sm focus:border-custom-primary/30 focus:outline-none focus:ring-2 focus:ring-custom-primary/15"
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto border-t border-gray-100">
              {loadingEmailList && typedEmailBroadcasts.length === 0 ? (
                <SidebarSkeleton />
              ) : (
                <ul className="py-1">
                  {emailComposeMode && (
                    <li>
                      <button
                        type="button"
                        onClick={() => {
                          setEmailMobilePanel('detail');
                          focusCompose();
                        }}
                        className="flex w-full gap-3 border-l-4 border-custom-secondary bg-custom-accent-light/60 px-4 py-3 text-left"
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-custom-secondary text-white">
                          <Mail size={16} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-gray-900">
                            {hasEmailDraft
                              ? emailSubject.trim() || t('draftNoSubject')
                              : t('draftNew')}
                          </p>
                          <p className="truncate text-xs text-gray-600">
                            {hasEmailDraft
                              ? emailBody.trim().slice(0, 80) || t('draftEmptyBody')
                              : audienceLabel(emailAudience)}
                          </p>
                        </div>
                      </button>
                    </li>
                  )}

                  {filteredEmails.length === 0 && !emailComposeMode ? (
                    <li className="px-4 py-8 text-center text-sm text-gray-500">
                      {emailSearch ? t('noSearchResults') : t('noEmails')}
                    </li>
                  ) : (
                    filteredEmails.map((email) => {
                      const active = !emailComposeMode && selectedEmailId === email.id;
                      return (
                        <li key={email.id}>
                          <button
                            type="button"
                            onClick={() => openSentEmail(email.id)}
                            className={cx(
                              'flex w-full gap-3 px-4 py-3 text-left transition-colors border-l-4',
                              active
                                ? 'border-custom-primary bg-blue-50/80'
                                : 'border-transparent hover:bg-gray-100/80'
                            )}
                          >
                            <div
                              className={cx(
                                'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                                active ? 'bg-custom-primary text-white' : 'bg-gray-200 text-gray-600'
                              )}
                            >
                              {initials(email.subject)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-baseline justify-between gap-2">
                                <span
                                  className={cx(
                                    'truncate text-sm',
                                    active ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'
                                  )}
                                >
                                  {email.subject}
                                </span>
                                <span className="shrink-0 text-[11px] text-gray-400">
                                  {formatWhen(email.createdAt)}
                                </span>
                              </div>
                              <p className="truncate text-xs text-gray-500">
                                {audienceLabel(email.audience)} · {email.sent}/{email.total}
                              </p>
                              <p className="mt-0.5 truncate text-xs text-gray-400">
                                {email.bodyPreview || '—'}
                              </p>
                            </div>
                            {email.failed > 0 && (
                              <span className="self-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                                !
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
              )}
            </div>
          </aside>

          {/* Right: compose | read | empty */}
          <section
            className={cx(
              'flex min-h-0 min-w-0 flex-col bg-white',
              emailMobilePanel === 'list' && 'hidden lg:flex'
            )}
          >
            <header className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-3 py-2 sm:px-4">
              <button
                type="button"
                onClick={() => setEmailMobilePanel('list')}
                className="lg:hidden flex h-9 w-9 shrink-0 items-center justify-center rounded-full hover:bg-gray-100"
                aria-label={t('backToList')}
              >
                <ArrowLeft size={18} />
              </button>
              {emailComposeMode ? (
                <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium text-gray-700">
                    {t('composeEmail')}
                  </span>
                  {hasEmailDraft && (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(t('discardDraft'))) {
                          resetComposeDraft();
                          focusCompose();
                        }
                      }}
                      className="shrink-0 text-xs font-medium text-gray-500 hover:text-red-600"
                    >
                      {t('discard')}
                    </button>
                  )}
                </div>
              ) : selectedEmail ? (
                <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900 sm:text-lg">
                  {selectedEmail.subject}
                </h2>
              ) : (
                <span className="text-sm text-gray-500">{t('selectEmailHint')}</span>
              )}
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {emailComposeMode ? (
                <form onSubmit={handleBroadcastEmail} className="flex h-full min-h-0 flex-col">
                  <div className="shrink-0 divide-y divide-gray-100 border-b border-gray-100">
                    <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
                      <span className="w-14 shrink-0 text-sm font-medium text-gray-500">
                        {t('to')}
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {(
                          [
                            { value: 'buyers' as const, icon: User, label: t('buyers') },
                            { value: 'sellers' as const, icon: Store, label: t('sellersAudience') },
                            { value: 'everyone' as const, icon: Users, label: t('everyone') },
                          ] as const
                        ).map(({ value, icon: Icon, label }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setEmailAudience(value)}
                            className={cx(
                              'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                              emailAudience === value
                                ? 'bg-custom-primary text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            )}
                          >
                            <Icon size={14} />
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 px-4 py-2">
                      <span className="w-14 shrink-0 text-sm font-medium text-gray-500">
                        {t('subject')}
                      </span>
                      <input
                        ref={emailSubjectRef}
                        type="text"
                        required
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        placeholder={t('subjectPlaceholder')}
                        className="min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                      />
                    </div>
                  </div>

                  <textarea
                    ref={emailBodyRef}
                    required
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    placeholder={t('bodyPlaceholder')}
                    className="min-h-[200px] flex-1 resize-none border-0 bg-white px-4 py-4 text-sm leading-relaxed text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 sm:px-6 sm:py-5"
                  />

                  <div className="shrink-0 border-t border-gray-100 bg-gray-50/80 px-4 py-3 sm:px-6">
                    <p className="mb-3 flex items-start gap-2 text-xs text-gray-500">
                      <AlertCircle size={14} className="mt-0.5 shrink-0" />
                      {t('emailNotice')}
                    </p>
                    {emailResult && (
                      <div
                        className={cx(
                          'mb-3 flex gap-2 rounded-lg px-3 py-2 text-sm',
                          emailResult.type === 'success' && 'bg-green-50 text-green-800',
                          emailResult.type === 'warning' && 'bg-amber-50 text-amber-900',
                          emailResult.type === 'error' && 'bg-red-50 text-red-800'
                        )}
                      >
                        {emailResult.type === 'success' ? (
                          <CheckCircle2 className="shrink-0" size={16} />
                        ) : (
                          <AlertCircle className="shrink-0" size={16} />
                        )}
                        <p>{emailResult.text}</p>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="submit"
                        size="sm"
                        disabled={emailSending}
                        loading={emailSending}
                        icon={Send}
                        iconPosition="right"
                      >
                        {emailSending ? t('sending') : t('sendEmail')}
                      </Button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!hasEmailDraft || window.confirm(t('discardDraft'))) {
                            resetComposeDraft();
                            setEmailComposeMode(false);
                            setEmailMobilePanel('list');
                          }
                        }}
                        className="rounded-xl px-4 py-2 text-sm text-gray-600 hover:bg-gray-200/80"
                      >
                        {t('cancel')}
                      </button>
                    </div>
                  </div>
                </form>
              ) : loadingEmailDetail ? (
                <div className="flex h-full items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-custom-primary" />
                </div>
              ) : selectedEmail ? (
                <div className="mx-auto w-full max-w-3xl">
                  <div className="border-b border-gray-100 px-4 py-4 sm:px-6">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                      <span>
                        <span className="font-medium text-gray-500">{t('to')}: </span>
                        {audienceLabel(selectedEmail.audience)}
                      </span>
                      <span className="text-gray-300">|</span>
                      <span>{new Date(selectedEmail.createdAt).toLocaleString()}</span>
                      <span className="text-gray-300">|</span>
                      <span>
                        {t('emailRecipients', {
                          sent: selectedEmail.sent,
                          total: selectedEmail.total,
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="px-4 py-6 sm:px-6">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800 sm:text-base">
                      {selectedEmail.text || selectedEmail.bodyPreview || '—'}
                    </p>
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon={Inbox}
                  title={t('selectEmailHint')}
                  description={t('composeEmailHint')}
                />
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default AdminMessaging;
